/**
 * Wallet.jsx — customer wallet + gift-card page.
 *
 * Features:
 *   - Balance card with usage note ("only for delivery")
 *   - "Load wallet" form (Stripe checkout redirect)
 *   - "Buy gift card" form (Stripe checkout redirect)
 *   - "Redeem gift code" form
 *   - Recent activity ledger
 *
 * On return from Stripe (URL param `session_id`), we call
 * `GET /wallet/session/:id` which finalises the topup / issues the gift.
 */
import React, { useCallback, useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';
import { Wallet as WalletIcon, Gift, Ticket, ArrowUpRight, ArrowDownRight, Loader2, Copy, ShieldCheck } from 'lucide-react';
import api from '../services/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';

const PRESETS = [10, 25, 50, 100];

const Wallet = () => {
  const [params, setParams] = useSearchParams();
  const [state, setState] = useState({ balance: 0, currency: 'CAD', ledger: [], gifts_bought: 0, usage_note: '' });
  const [loading, setLoading] = useState(true);
  const [topupAmt, setTopupAmt] = useState(25);
  const [giftAmt, setGiftAmt] = useState(50);
  const [giftRecipient, setGiftRecipient] = useState('');
  const [giftName, setGiftName] = useState('');
  const [redeemCode, setRedeemCode] = useState('');
  const [busy, setBusy] = useState({});
  const [issuedGift, setIssuedGift] = useState(null); // { code, amount } after purchase

  const load = useCallback(async () => {
    try {
      const r = await api.get('/wallet/me');
      setState(r.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not load wallet');
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  // Finalise Stripe return.
  useEffect(() => {
    const sid = params.get('session_id');
    if (!sid) return;
    (async () => {
      try {
        const r = await api.get(`/wallet/session/${sid}`);
        if (r.data?.ok) {
          if (r.data.kind === 'wallet_topup') {
            toast.success(`Loaded $${Number(r.data.credited).toFixed(2)} into your wallet.`);
          } else if (r.data.kind === 'gift_card') {
            setIssuedGift({ code: r.data.gift_code, amount: r.data.amount });
            toast.success('Gift card ready — share the code with the recipient.');
          }
          load();
        } else {
          toast.info('Payment not confirmed yet — refresh in a moment.');
        }
      } catch (e) {
        toast.error(e?.response?.data?.detail || 'Could not finalise payment');
      } finally {
        setParams((p) => { p.delete('session_id'); return p; }, { replace: true });
      }
    })();
  }, [params, setParams, load]);

  const startTopup = async () => {
    if (topupAmt < 5 || topupAmt > 500) { toast.error('Amount must be $5–$500'); return; }
    setBusy((b) => ({ ...b, topup: true }));
    try {
      const r = await api.post('/wallet/load/session', { amount: Number(topupAmt), origin_url: window.location.origin });
      window.location.assign(r.data.url);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not start top-up');
      setBusy((b) => ({ ...b, topup: false }));
    }
  };

  const startGift = async () => {
    if (giftAmt < 5 || giftAmt > 500) { toast.error('Gift card amount must be $5–$500'); return; }
    setBusy((b) => ({ ...b, gift: true }));
    try {
      const r = await api.post('/wallet/gift/session', {
        amount: Number(giftAmt),
        origin_url: window.location.origin,
        recipient_email: giftRecipient?.trim() || undefined,
        recipient_name: giftName?.trim() || undefined,
      });
      window.location.assign(r.data.url);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not start gift-card purchase');
      setBusy((b) => ({ ...b, gift: false }));
    }
  };

  const doRedeem = async () => {
    if (!redeemCode.trim()) { toast.error('Enter a code first'); return; }
    setBusy((b) => ({ ...b, redeem: true }));
    try {
      const r = await api.post('/wallet/gift/redeem', { code: redeemCode.trim() });
      toast.success(`Credited $${Number(r.data.credited_amount).toFixed(2)} — new balance $${Number(r.data.new_balance).toFixed(2)}`);
      setRedeemCode('');
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Redemption failed');
    } finally {
      setBusy((b) => ({ ...b, redeem: false }));
    }
  };

  const copyCode = (code) => {
    try { navigator.clipboard.writeText(code); toast.success('Code copied'); } catch { /* ignore */ }
  };

  return (
    <div className="max-w-5xl mx-auto px-4 sm:px-6 py-8" data-testid="wallet-page">
      <div className="flex items-center gap-3 mb-6">
        <div className="h-10 w-10 rounded-xl bg-emerald-600 text-white flex items-center justify-center">
          <WalletIcon className="h-5 w-5" />
        </div>
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Wallet & gift cards</h1>
          <p className="text-sm text-slate-500">Load funds for delivery fees or send a ShipitNow gift.</p>
        </div>
      </div>

      {/* Balance card */}
      <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm" data-testid="wallet-balance-card">
        <div className="flex items-baseline justify-between">
          <div>
            <div className="text-xs uppercase tracking-wider text-slate-500">Available balance</div>
            <div className="mt-1 text-4xl font-bold text-slate-900" data-testid="wallet-balance-amount">
              {loading ? <Loader2 className="h-6 w-6 animate-spin text-slate-400" /> : `$${Number(state.balance || 0).toFixed(2)}`}
              <span className="text-base font-normal text-slate-500 ml-1.5">{state.currency}</span>
            </div>
          </div>
          <div className="hidden sm:flex items-center gap-1.5 text-[11px] font-medium text-emerald-800 bg-emerald-50 border border-emerald-200 rounded-full px-2.5 py-1">
            <ShieldCheck className="h-3.5 w-3.5" /> Delivery-only
          </div>
        </div>
        <p className="mt-3 text-xs text-slate-500">{state.usage_note || 'Wallet funds can only be used to pay for delivery — item subtotals still require a card.'}</p>
      </div>

      {/* Gift issued after purchase */}
      {issuedGift && (
        <div className="mt-4 rounded-xl border border-emerald-300 bg-emerald-50/60 p-4" data-testid="issued-gift-card">
          <div className="flex items-center gap-2 text-emerald-900 font-semibold">
            <Gift className="h-4 w-4" /> Gift card ready — ${Number(issuedGift.amount).toFixed(2)} CAD
          </div>
          <div className="mt-2 flex items-center gap-2">
            <code className="flex-1 text-sm font-mono bg-white border border-emerald-200 rounded-lg px-3 py-2 select-all" data-testid="issued-gift-code">{issuedGift.code}</code>
            <Button size="sm" variant="outline" onClick={() => copyCode(issuedGift.code)} data-testid="copy-gift-code">
              <Copy className="h-4 w-4 mr-1" /> Copy
            </Button>
          </div>
          <p className="text-[11px] text-emerald-800/80 mt-2">Share this code with the recipient. They redeem it on their Wallet page.</p>
        </div>
      )}

      <div className="grid md:grid-cols-2 gap-4 mt-6">
        {/* Load wallet */}
        <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm" data-testid="wallet-load-card">
          <div className="flex items-center gap-2 text-slate-900 font-semibold">
            <ArrowUpRight className="h-4 w-4 text-emerald-600" /> Load wallet
          </div>
          <p className="text-xs text-slate-500 mt-1">Top up between $5 and $500 CAD from your card.</p>
          <div className="mt-3 flex flex-wrap gap-2">
            {PRESETS.map((v) => (
              <button
                key={v}
                onClick={() => setTopupAmt(v)}
                data-testid={`topup-preset-${v}`}
                className={`px-3 h-9 rounded-lg border text-sm font-medium transition-colors ${Number(topupAmt) === v ? 'bg-emerald-600 border-emerald-600 text-white' : 'bg-white border-slate-200 text-slate-700 hover:bg-slate-50'}`}
              >
                ${v}
              </button>
            ))}
          </div>
          <div className="mt-3 flex items-end gap-2">
            <div className="flex-1">
              <Label className="text-[11px]">Amount (CAD)</Label>
              <Input
                type="number" min={5} max={500} step={5}
                value={topupAmt}
                onChange={(e) => setTopupAmt(e.target.value)}
                data-testid="topup-amount-input"
              />
            </div>
            <Button
              className="h-10 bg-emerald-600 hover:bg-emerald-700"
              onClick={startTopup} disabled={busy.topup}
              data-testid="topup-submit-btn"
            >
              {busy.topup ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Top up'}
            </Button>
          </div>
        </div>

        {/* Buy gift card */}
        <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm" data-testid="wallet-gift-card">
          <div className="flex items-center gap-2 text-slate-900 font-semibold">
            <Gift className="h-4 w-4 text-orange-500" /> Buy a gift card
          </div>
          <p className="text-xs text-slate-500 mt-1">Purchase a code the recipient redeems into their own wallet.</p>
          <div className="mt-3 flex flex-wrap gap-2">
            {PRESETS.map((v) => (
              <button
                key={v}
                onClick={() => setGiftAmt(v)}
                data-testid={`gift-preset-${v}`}
                className={`px-3 h-9 rounded-lg border text-sm font-medium transition-colors ${Number(giftAmt) === v ? 'bg-slate-900 border-slate-900 text-white' : 'bg-white border-slate-200 text-slate-700 hover:bg-slate-50'}`}
              >
                ${v}
              </button>
            ))}
          </div>
          <div className="mt-3 grid grid-cols-2 gap-2">
            <div>
              <Label className="text-[11px]">Amount (CAD)</Label>
              <Input
                type="number" min={5} max={500} step={5}
                value={giftAmt}
                onChange={(e) => setGiftAmt(e.target.value)}
                data-testid="gift-amount-input"
              />
            </div>
            <div>
              <Label className="text-[11px]">Recipient email (optional)</Label>
              <Input
                type="email"
                placeholder="friend@example.com"
                value={giftRecipient}
                onChange={(e) => setGiftRecipient(e.target.value)}
                data-testid="gift-recipient-input"
              />
            </div>
            <div className="col-span-2">
              <Label className="text-[11px]">Recipient name (optional)</Label>
              <Input
                value={giftName}
                onChange={(e) => setGiftName(e.target.value)}
                placeholder="For your friend"
                data-testid="gift-name-input"
              />
            </div>
          </div>
          <Button
            className="mt-3 w-full bg-slate-900 hover:bg-slate-800"
            onClick={startGift} disabled={busy.gift}
            data-testid="gift-submit-btn"
          >
            {busy.gift ? <Loader2 className="h-4 w-4 animate-spin" /> : `Buy $${giftAmt} gift card`}
          </Button>
        </div>
      </div>

      {/* Redeem */}
      <div className="rounded-2xl border border-slate-200 bg-white p-5 mt-4 shadow-sm" data-testid="wallet-redeem-card">
        <div className="flex items-center gap-2 text-slate-900 font-semibold">
          <Ticket className="h-4 w-4 text-emerald-600" /> Redeem a gift-card code
        </div>
        <p className="text-xs text-slate-500 mt-1">Codes look like <span className="font-mono">SHIP-XXXX-XXXX-XXXX</span>. Redeeming credits your wallet immediately.</p>
        <div className="mt-3 flex flex-col sm:flex-row gap-2">
          <Input
            value={redeemCode}
            onChange={(e) => setRedeemCode(e.target.value)}
            placeholder="SHIP-XXXX-XXXX-XXXX"
            className="uppercase font-mono"
            data-testid="redeem-code-input"
          />
          <Button
            className="bg-emerald-600 hover:bg-emerald-700"
            onClick={doRedeem} disabled={busy.redeem}
            data-testid="redeem-submit-btn"
          >
            {busy.redeem ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Redeem'}
          </Button>
        </div>
      </div>

      {/* Ledger */}
      <div className="mt-6" data-testid="wallet-ledger">
        <h2 className="text-lg font-semibold text-slate-900 mb-2">Recent activity</h2>
        {(!state.ledger || state.ledger.length === 0) ? (
          <div className="text-sm text-slate-500 rounded-xl border border-dashed border-slate-200 p-6 text-center bg-white">
            No wallet activity yet. Top up or redeem a gift card to get started.
          </div>
        ) : (
          <div className="rounded-xl border border-slate-200 divide-y divide-slate-100 bg-white overflow-hidden">
            {state.ledger.map((row, i) => {
              const amt = Number(row.amount || 0);
              const isCredit = row.type === 'credit';
              return (
                <div key={i} className="flex items-center gap-3 px-4 py-3" data-testid={`ledger-row-${i}`}>
                  <div className={`h-8 w-8 rounded-full flex items-center justify-center ${isCredit ? 'bg-emerald-50 text-emerald-600' : 'bg-slate-100 text-slate-600'}`}>
                    {isCredit ? <ArrowUpRight className="h-4 w-4" /> : <ArrowDownRight className="h-4 w-4" />}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-medium text-slate-900 capitalize">{(row.reason || row.type || 'Transaction').replace(/_/g, ' ')}</div>
                    <div className="text-[11px] text-slate-500">{new Date(row.created_at).toLocaleString()}</div>
                  </div>
                  <div className={`text-sm font-semibold ${isCredit ? 'text-emerald-700' : 'text-slate-700'}`}>
                    {isCredit ? '+' : '−'}${Math.abs(amt).toFixed(2)}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default Wallet;
